import { useQuery } from '@tanstack/react-query';

import { ActionText } from '@components/action-text';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconRemove } from '@components/icons/remove';
import { Menu } from '@components/menu';
import { PageContainers } from '@components/page-containers';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';

import { api } from 'api';
import { Loading } from 'components/Loading';
import { PageContent } from 'components/Page';
import { useModal } from 'features/modals';
import { useStore } from 'models/Provider';

export const UserApps = () => {
  const { t } = useStore();
  const { showModal } = useModal();
  const userAppsQuery = useQuery({
    queryKey: ['userApps'],
    queryFn: () => api.users.appList(),
  });
  if (userAppsQuery.isLoading) {
    return <Loading />;
  }
  if (!userAppsQuery.isSuccess) {
    return <Text text={t('profile.apps.loading.error')} />;
  }
  return (
    <PageContent>
      <PageContainers.MiddleColumnPage>
        <Stack space={2}>
          <Text font="bold18" text={t('profile.apps.heading')} />
          <Text font="regular14" color="textSecondary" text={t('profile.apps.subheader')} />
          <ActionText.ExternalLink font="regular14" href={'https://api.50skills.app/docs'}>
            {t('profile.apps.subheader.docs')}
          </ActionText.ExternalLink>
        </Stack>
        <Separator />
        <Stack space={2}>
          <Text font="bold18" text={t('profile.apps.personal')} />
          <Text font="regular14" color="textSecondary" text={t('profile.apps.personal.info')} />
        </Stack>
        <Stack space={6}>
          {userAppsQuery.data.map((app) => (
            <UserApp key={app.clientId} clientId={app.clientId} clientName={app.clientName} />
          ))}
        </Stack>
        <Stack space={0} orientation="horizontal" justify="flex-start">
          <Button
            variant="secondary"
            iconLeft={<IconAddPlus />}
            text={t('profile.apps.add')}
            onClick={() => showModal('ADD_USER_APP')}
          />
        </Stack>
      </PageContainers.MiddleColumnPage>
    </PageContent>
  );
};

type UserAppProps = {
  clientId: string;
  clientName: string;
};

const UserApp = (props: UserAppProps) => {
  const { clientId, clientName } = props;
  const { t } = useStore();
  const { showModal } = useModal();
  return (
    <Card space={4}>
      <Stack space={0} orientation="horizontal" justify="space-between" align="center">
        <Stack space={2}>
          <Text font="semibold14" text={clientName} />
          <Text font="regular14" color="purple80" text={clientId} />
        </Stack>
        <Menu buttonProps={{ variant: 'ghost', iconLeft: <IconMoreVertical /> }}>
          <Menu.Item
            onSelect={() => showModal('DELETE_USER_APP', { clientId })}
            label={t('profile.apps.menu.remove')}
            icon={<IconRemove />}
            variant="danger"
          />
        </Menu>
      </Stack>
    </Card>
  );
};
